import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";

import { LazyLoading } from "@components/lazy-loading";
import { ErrorPage } from "@views/shared";

import { ContainerProvider } from "./contexts/container-context";
import NotificationProvider from "./contexts/notification-context";
import { initIcons } from "./lib/fontawesome-icons";
import { queryClient } from "./lib/react-query/client";
import {
  initStaleChunkRecovery,
  recoverFromStaleChunk,
} from "./lib/stale-chunk";
import { initializeObservability } from "./observability";
import Router from "./router";

import "./index.scss";

initializeObservability();
initStaleChunkRecovery();
initIcons();

// Log the app version
console.info(
  `%cTransfr Dashboard %cv${dashboardAppVersion}`,
  "font-weight:bold",
  "font-weight:normal"
);

const container = document.querySelector("#root");
createRoot(container).render(
  <QueryClientProvider client={queryClient}>
    <ContainerProvider>
      <NotificationProvider>
        <ErrorBoundary
          FallbackComponent={ErrorPage}
          onError={(error, info) => {
            console.error(error, info);
            Sentry.captureException(error, { extra: info });
            // Reported before reloading, so the chunk failure still reaches
            // Sentry rather than vanishing with the document.
            recoverFromStaleChunk(error);
          }}
        >
          <Suspense fallback={<LazyLoading />}>
            <Router />
          </Suspense>
        </ErrorBoundary>
      </NotificationProvider>
    </ContainerProvider>
    {process.env.NODE_ENV === "development" && (
      <ReactQueryDevtools initialIsOpen={false} />
    )}
  </QueryClientProvider>
);
